import { createSlice } from '@reduxjs/toolkit';
import {
  AddFinalInspectionList,
  fetchFinalInspectionCompletedStages,
  fetchFinalInspectionFlowHistory,
  fetchFinalInspectionForm,
  fetchFinalInspectionList,
  fetchLastFrameQciData,
  viewFinalInspectionForm,
  fetchInspectedShellTypes,
  fetchPreceilingReportData,
  fetchInspectedShellTypesForQuality,
  fetchFinalReportData, 
} from './finalInspectionThunk';

const initialState = {
  finalInspection: {
    loading: false,
    success: false,
    error: null,
    data: [],
  },
  inspectedShellTypes: {
    loading: false,
    success: false,
    error: null,
    data: [],
  },
  inspectedShellTypesForQuality: {
    loading: false,
    success: false,
    error: null,
    data: [],
  },
  addFinalInspection: {
    loading: false,
    success: false,
    error: null,
    data: [],
  },
  listFinalInspection: {
    loading: false,
    success: false,
    error: null,
    data: [],
  },
  viewFinalInspection: {
    loading: false,
    success: false,
    error: null,
    data: [],
  },
  completedStages: {
    loading: false,
    success: false,
    error: null,
    data: [],
  },
  lastFrameQciData: {
    loading: false,
    success: false,
    error: null,
    data: [],
  },
  fetchInspectionData: {
    loading: false,
    success: false,
    error: null,
    data: [],
  },
  plugDoorData: {
    loading: false,
    success: false,
    error: null,
    data: [],
  },
  shellNumbers: [],
  coachNumbers: [],
  loading: false,
  error: null,
  flowHistory: [],
  // Add preceilingReport state
  preceilingReport: {
    loading: false,
    success: false,
    error: null,
    data: [],
    // pagination: {
    //   current_page: 1,
    //   per_page: 10,
    //   total: 0,
    //   total_pages: 0,
    // },
  },
  // Add finalReport state
  finalReport: {
    loading: false,
    success: false,
    error: null,
    data: [],
    pagination: {
      current_page: 1,
      per_page: 10,
      total: 0,
      total_pages: 0,
    },
  },
};

const finalInspectionSlice = createSlice({
  name: 'finalInspection',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFinalInspectionForm.pending, (state) => {
        state.finalInspection.loading = true;
        state.finalInspection.error = null;
      })
      .addCase(fetchFinalInspectionForm.fulfilled, (state, action) => {
        state.finalInspection.loading = false;
        state.finalInspection.data = action.payload;
      })
      .addCase(fetchFinalInspectionForm.rejected, (state, action) => {
        state.finalInspection.loading = false;
        state.finalInspection.error = action.payload || 'Failed to fetch materials';
      })
      .addCase(fetchFinalInspectionList.pending, (state) => {
        state.listFinalInspection.loading = true;
        state.listFinalInspection.error = null;
      })
      .addCase(fetchFinalInspectionList.fulfilled, (state, action) => {
        state.listFinalInspection.loading = false;
        state.listFinalInspection.data = action.payload;
        state.pagination = {
          total: action.payload.total,
          total_pages: action.payload.total_pages,
          current_page: action.payload.current_page,
          per_page: action.payload.per_page,
        };
      })
      .addCase(fetchFinalInspectionList.rejected, (state, action) => {
        state.listFinalInspection.loading = false;
        state.listFinalInspection.error = action.payload || 'Failed to fetch materials';
      })
      .addCase(AddFinalInspectionList.pending, (state) => {
        state.addFinalInspection.loading = true;
        state.addFinalInspection.error = null;
      })
      .addCase(AddFinalInspectionList.fulfilled, (state, action) => {
        state.addFinalInspection.loading = false;
        state.addFinalInspection.data = action.payload;
      })
      .addCase(AddFinalInspectionList.rejected, (state, action) => {
        state.addFinalInspection.loading = false;
        state.addFinalInspection.error = action.payload || 'Failed to fetch materials';
      })
      .addCase(viewFinalInspectionForm.pending, (state) => {
        state.viewFinalInspection.loading = true;
        state.viewFinalInspection.error = null;
      })
      .addCase(viewFinalInspectionForm.fulfilled, (state, action) => {
        state.viewFinalInspection.loading = false;
        state.viewFinalInspection.data = action.payload;
      })
      .addCase(viewFinalInspectionForm.rejected, (state, action) => {
        state.viewFinalInspection.loading = false;
        state.viewFinalInspection.error = action.payload || 'Failed to fetch materials';
      })
      .addCase(fetchFinalInspectionFlowHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFinalInspectionFlowHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.flowHistory = action.payload.data;
      })
      .addCase(fetchFinalInspectionFlowHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch materials';
      })
      .addCase(fetchFinalInspectionCompletedStages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFinalInspectionCompletedStages.fulfilled, (state, action) => {
        state.loading = false;
        state.completedStages = action.payload;
      })
      .addCase(fetchFinalInspectionCompletedStages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch materials';
      })
      .addCase(fetchLastFrameQciData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLastFrameQciData.fulfilled, (state, action) => {
        state.loading = false;
        state.lastFrameQciData.data = action.payload?.data;
      })
      .addCase(fetchLastFrameQciData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch materials';
      })
      // Inspected Shell Types Cases
      .addCase(fetchInspectedShellTypes.pending, (state) => {
        state.inspectedShellTypes.loading = true;
        state.inspectedShellTypes.error = null;
        state.inspectedShellTypes.success = false;
      })
      .addCase(fetchInspectedShellTypes.fulfilled, (state, action) => {
        state.inspectedShellTypes.loading = false;
        state.inspectedShellTypes.success = true;
        state.inspectedShellTypes.data = action.payload?.data || [];
      })
      .addCase(fetchInspectedShellTypes.rejected, (state, action) => {
        state.inspectedShellTypes.loading = false;
        state.inspectedShellTypes.success = false;
        state.inspectedShellTypes.error = action.payload || 'Failed to fetch inspected shell types';
      })
      // Preceiling Report Cases
      .addCase(fetchPreceilingReportData.pending, (state) => {
        state.preceilingReport.loading = true;
        state.preceilingReport.error = null;
        state.preceilingReport.success = false;
      })
      .addCase(fetchPreceilingReportData.fulfilled, (state, action) => {
        state.preceilingReport.loading = false;
        state.preceilingReport.success = true;
        state.preceilingReport.data = action.payload?.data || [];
        // state.preceilingReport.pagination = {
        //   current_page: action.payload?.current_page || 1,
        //   per_page: action.payload?.per_page || 10,
        //   total: action.payload?.total || 0,
        //   total_pages: action.payload?.total_pages || 0,
        // };
      })
      .addCase(fetchPreceilingReportData.rejected, (state, action) => {
        state.preceilingReport.loading = false;
        state.preceilingReport.success = false;
        state.preceilingReport.error = action.payload || 'Failed to fetch preceiling report data';
      })
      // Inspected Shell Types for QFR Final Quality
      .addCase(fetchInspectedShellTypesForQuality.pending, (state) => {
        state.inspectedShellTypesForQuality.loading = true;
        state.inspectedShellTypesForQuality.error = null;
        state.inspectedShellTypesForQuality.success = false;
      })
      .addCase(fetchInspectedShellTypesForQuality.fulfilled, (state, action) => {
        state.inspectedShellTypesForQuality.loading = false;
        state.inspectedShellTypesForQuality.success = true;
        state.inspectedShellTypesForQuality.data = action.payload?.data || [];
      })
      .addCase(fetchInspectedShellTypesForQuality.rejected, (state, action) => {
        state.inspectedShellTypesForQuality.loading = false;
        state.inspectedShellTypesForQuality.success = false;
        state.inspectedShellTypesForQuality.error = action.payload || 'Failed to fetch inspected shell types';
      })
      // Final Report Cases
      .addCase(fetchFinalReportData.pending, (state) => {
        state.finalReport.loading = true;
        state.finalReport.error = null;
        state.finalReport.success = false;
      })
      .addCase(fetchFinalReportData.fulfilled, (state, action) => {
        state.finalReport.loading = false;
        state.finalReport.success = true;
        state.finalReport.data = action.payload?.data || [];
        // state.finalReport.pagination = {
        //   current_page: action.payload?.current_page || 1,
        //   per_page: action.payload?.per_page || 10,
        //   total: action.payload?.total || 0,
        //   total_pages: action.payload?.total_pages || 0,
        // };
      })
      .addCase(fetchFinalReportData.rejected, (state, action) => {
        state.finalReport.loading = false;
        state.finalReport.success = false;
        state.finalReport.error = action.payload || 'Failed to fetch final report data';
      });
  },
});

export default finalInspectionSlice.reducer;